import React, { useState, useEffect, useCallback, useMemo } from 'react';
import apiClient from '../../../api/apiClient';
import LoadingSpinner from '../../../components/ui/LoadingSpinner';
import ErrorBanner from '../../../components/ui/ErrorBanner';
import { st, colors } from '../shared/meetingStyles';
import Avatar from '../shared/Avatar';
import { TargetIcon, PlusIcon, XIcon } from '../shared/Icons';

function currentQuarter() {
  const d = new Date();
  const q = Math.floor(d.getMonth() / 3) + 1;
  return `Q${q}-${d.getFullYear()}`;
}

function MilestoneBar({ milestones }) {
  if (!milestones || milestones.length === 0) return null;
  return (
    <div style={{ display: 'flex', gap: '3px', marginTop: '0.35rem' }}>
      {milestones.map(m => (
        <div key={m.id} style={{ flex: 1, height: '6px', borderRadius: '3px', background: m.completed ? colors.green : '#e2e8f0' }} title={m.title} />
      ))}
    </div>
  );
}

function AddRockForm({ team, onAdd, onCancel }) {
  const [title, setTitle] = useState('');
  const [ownerUserId, setOwnerUserId] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setSaving(true);
    try {
      await onAdd({ title: title.trim(), owner_user_id: ownerUserId || null, due_date: dueDate || null, quarter: currentQuarter() });
      onCancel();
    } catch {} finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', padding: '0.85rem 1rem', background: colors.bgSubtle, borderRadius: '0.75rem', border: `1.5px solid ${colors.border}`, flexWrap: 'wrap', marginBottom: '0.9rem' }}>
      <select value={ownerUserId} onChange={e => setOwnerUserId(e.target.value)} style={{ ...st.input, width: '150px' }}>
        <option value="">No owner</option>
        {team.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
      </select>
      <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Rock title" style={{ ...st.input, flex: 1, minWidth: '200px' }} autoFocus />
      <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} style={st.input} />
      <button type="submit" disabled={saving} style={st.primaryBtn}>{saving ? '...' : 'Add'}</button>
      <button type="button" onClick={onCancel} style={st.secondaryBtn}>Cancel</button>
    </form>
  );
}

function AddMilestoneRow({ rockId, onAdd }) {
  const [title, setTitle] = useState('');
  const [dueDate, setDueDate] = useState('');
  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    await onAdd(rockId, { title: title.trim(), due_date: dueDate || null });
    setTitle(''); setDueDate('');
  };
  return (
    <form onSubmit={submit} style={{ display: 'flex', gap: '0.4rem', marginTop: '0.4rem' }}>
      <input value={title} onChange={e => setTitle(e.target.value)} placeholder="+ Add milestone" style={{ ...st.input, flex: 1, fontSize: '0.75rem', padding: '0.3rem 0.5rem' }} />
      <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} style={{ ...st.input, fontSize: '0.75rem', padding: '0.3rem 0.5rem' }} />
      <button type="submit" style={{ ...st.secondaryBtn, padding: '0.3rem 0.6rem', fontSize: '0.7rem' }}>Add</button>
    </form>
  );
}

export default function GoalsSection() {
  const [rocks, setRocks] = useState([]);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [rocksRes, teamRes] = await Promise.all([
        apiClient.get(`/meetings/rocks?quarter=${currentQuarter()}`),
        apiClient.get('/meetings/team'),
      ]);
      setRocks(rocksRes.data.rocks || []);
      setTeam(teamRes.data.users || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const byOwner = useMemo(() => {
    const groups = {};
    for (const r of rocks) {
      const key = r.owner_name || 'Unassigned';
      (groups[key] ||= []).push(r);
    }
    return groups;
  }, [rocks]);

  const addRock = async (data) => { await apiClient.post('/meetings/rocks', data); await load(); };
  const updateRockStatus = async (id, status) => { await apiClient.put(`/meetings/rocks/${id}`, { status }); await load(); };
  const deleteRock = async (id) => {
    if (!window.confirm('Delete this rock?')) return;
    await apiClient.delete(`/meetings/rocks/${id}`); await load();
  };
  const addMilestone = async (rockId, data) => { await apiClient.post(`/meetings/rocks/${rockId}/milestones`, data); await load(); };
  const toggleMilestone = async (id, completed) => { await apiClient.put(`/meetings/rocks/milestones/${id}`, { completed: !completed }); await load(); };

  if (loading) return <LoadingSpinner />;

  return (
    <div style={st.card}>
      {error && <ErrorBanner message={error} />}
      <div style={st.cardHeader}>
        <h2 style={{ ...st.h2, display: 'flex', alignItems: 'center', gap: '0.5rem' }}><TargetIcon size={18} /> Goals ({rocks.length})</h2>
        <button onClick={() => setShowAdd(s => !s)} style={{ ...st.primaryBtn, display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}><PlusIcon size={13} /> Add Rock</button>
      </div>
      {showAdd && <AddRockForm team={team} onAdd={addRock} onCancel={() => setShowAdd(false)} />}
      {rocks.length === 0 ? (
        <div style={st.emptyState}><TargetIcon size={28} style={{ color: colors.faint }} />No rocks for {currentQuarter()} yet.</div>
      ) : (
        Object.entries(byOwner).map(([owner, ownerRocks]) => (
          <div key={owner} style={{ marginBottom: '1.1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <Avatar name={owner === 'Unassigned' ? null : owner} size={20} />
              <span style={{ fontWeight: '800', fontSize: '0.78rem', color: colors.text }}>{owner}</span>
            </div>
            {ownerRocks.map(rock => {
              const badge = st.statusBadge(rock.status);
              const isExpanded = expanded[rock.id];
              return (
                <div key={rock.id} style={{ borderBottom: '1px solid #f1f5f9', padding: '0.7rem 0 0.7rem 1.75rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem' }}>
                    <div style={{ flex: 1, cursor: 'pointer' }} onClick={() => setExpanded(e => ({ ...e, [rock.id]: !e[rock.id] }))}>
                      <div style={{ fontSize: '0.82rem', fontWeight: '700', color: colors.text }}>{rock.title}</div>
                      {rock.due_date && <div style={st.subtext}>Due {rock.due_date}</div>}
                      <MilestoneBar milestones={rock.milestones} />
                    </div>
                    <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                      <select value={rock.status} onChange={e => updateRockStatus(rock.id, e.target.value)} style={badge.style}>
                        <option value="on_track">On track</option>
                        <option value="off_track">Off track</option>
                        <option value="done">Done</option>
                      </select>
                      <button onClick={() => deleteRock(rock.id)} style={st.ghostBtn}><XIcon size={13} /></button>
                    </div>
                  </div>
                  {isExpanded && (
                    <div style={{ marginTop: '0.5rem', paddingLeft: '0.5rem', borderLeft: `2px solid ${colors.border}` }}>
                      {(rock.milestones || []).map(m => (
                        <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.2rem 0 0.2rem 0.5rem' }}>
                          <input type="checkbox" checked={!!m.completed} onChange={() => toggleMilestone(m.id, m.completed)} />
                          <span style={{ fontSize: '0.75rem', textDecoration: m.completed ? 'line-through' : 'none', color: m.completed ? colors.faint : colors.text }}>{m.title}</span>
                          {m.due_date && <span style={{ fontSize: '0.65rem', color: colors.faint }}>({m.due_date})</span>}
                        </div>
                      ))}
                      <AddMilestoneRow rockId={rock.id} onAdd={addMilestone} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))
      )}
    </div>
  );
}
